<div>
  <!-- Toast notifications -->
  <p-toast position="top-right"></p-toast>

  <!-- Confirmation Dialog -->
  <p-confirmDialog></p-confirmDialog>

  <!-- Narrow Confirmation Dialog for publish -->
  <p-confirmDialog key="publishConfirm" styleClass="confirm-dialog-narrow"></p-confirmDialog>

  <!-- Main Content Layout -->
  <p-card class="w-full">
    <form [formGroup]="projectForm" (ngSubmit)="onSubmit()">
      <!-- Project Information Layout -->
      <p-accordion
        [value]="accordionValue()"
        (valueChange)="accordionValue.set($any($event))"
        class="mb-8"
      >
        <!-- Project Header Card -->
        <p-accordion-panel value="0">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.projectInformation' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <p-card>
              <!-- Main Layout: Single Column -->
              <div class="flex flex-col gap-8">
                <!-- Row 1: Image + Name/Owner Block -->
                <div class="flex flex-col md:flex-row items-start gap-6">
                  <!-- Project Icon/Logo Upload -->
                  <div class="flex-shrink-0">
                    @if (loading()) {
                    <!-- Skeleton for project image -->
                    <p-skeleton width="90px" height="90px" class="rounded-xl"></p-skeleton>
                    } @else {
                    <div
                      class="relative w-[90px] h-[90px] rounded-xl border-2 border-gray-600 overflow-hidden cursor-pointer group"
                      (click)="triggerImageUpload()"
                    >
                      @if (projectImagePreview()) {
                      <!-- Uploaded Image Display -->
                      <img
                        [src]="
                          projectImagePreview()
                            ? projectImagePreview()!.startsWith('data:') ||
                              projectImagePreview()!.startsWith('http')
                              ? projectImagePreview()!
                              : (projectImagePreview()!
                                | adminImageSrc : projectId : { v: lastSavedAt() })
                            : null
                        "
                        alt="Project Logo"
                        class="w-full h-full object-cover"
                      />

                      <!-- Edit Overlay on Hover -->
                      <div
                        class="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <div class="text-white text-center">
                          <i class="pi pi-camera text-xl mb-1 block"></i>
                          <span class="text-caption-sm">{{
                            'projectEdit.changeImage' | translate
                          }}</span>
                        </div>
                      </div>
                      } @else {
                      <!-- Upload Placeholder -->
                      <div
                        class="w-full h-full bg-surface-100 dark:bg-surface-800 flex items-center justify-center"
                      >
                        <i class="pi pi-image text-3xl text-surface-400"></i>
                      </div>

                      <!-- Upload Text on Hover -->
                      <div
                        class="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white"
                      >
                        <i class="pi pi-upload text-xl mb-1"></i>
                        <span class="text-caption-sm">{{
                          'projectEdit.uploadImageButton' | translate
                        }}</span>
                      </div>
                      }

                      <!-- Hidden File Input -->
                      <input
                        #fileInput
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                        (change)="onImageFileChange($event)"
                        class="hidden"
                        aria-label="Upload project image"
                      />
                    </div>

                    <!-- Upload Status/Error Messages -->
                    @if (projectImageError) {
                    <div class="mt-2 max-w-[90px]">
                      <p-message severity="error">{{ projectImageError | translate }}</p-message>
                    </div>
                    } }
                  </div>

                  <!-- Name and Owner Column -->
                  <div class="flex-1 max-w-2xl">
                    @if (loading()) {
                    <!-- Skeleton for Project Name -->
                    <div class="mb-6">
                      <label class="form-label mb-2 block">{{
                        'projectEdit.projectName' | translate
                      }}</label>
                      <p-skeleton width="100%" height="3.5rem" class="rounded-md"></p-skeleton>
                    </div>
                    } @else {
                    <!-- Project Name -->
                    <div class="mb-6">
                      <label for="projectName" class="form-label mb-2 block">{{
                        'projectEdit.projectName' | translate
                      }}</label>
                      <input
                        id="projectName"
                        type="text"
                        pInputText
                        formControlName="name"
                        maxlength="30"
                        (input)="onNameInput($event)"
                        (keydown)="onNameKeydown($event)"
                        (paste)="onNamePaste($event)"
                        class="w-full"
                      />
                      <div class="flex justify-between items-center mt-1">
                        <div>
                          @if (showNameMaxLengthError()) {
                          <p-message severity="error">{{
                            'projectEdit.errors.nameMaxLength' | translate
                          }}</p-message>
                          } @else if (projectForm.get('name')?.invalid &&
                          projectForm.get('name')?.touched) { @if
                          (projectForm.get('name')?.hasError('required')) {
                          <p-message severity="error">{{
                            'projectEdit.nameRequired' | translate
                          }}</p-message>
                          } }
                        </div>
                        <span class="text-sm text-muted">
                          {{ nameControl.value?.length || 0 }}/30
                        </span>
                      </div>
                    </div>

                    <!-- Project Owner -->
                    <div class="space-y-6">
                      <!-- Project Owner Search (AutoComplete) -->
                      <div>
                        <label for="projectOwner" class="form-label mb-2 block">
                          {{ 'projectEdit.selectProjectOwner' | translate }}
                          <span class="text-red-500">*</span>
                        </label>
                        <p-autocomplete
                          id="projectOwner"
                          [formControl]="projectOwnerEmailControl"
                          [suggestions]="projectOwnerSuggestions()"
                          (completeMethod)="searchProjectOwners($event)"
                          (onSelect)="onProjectOwnerSelect($event)"
                          (onClear)="onProjectOwnerClear()"
                          (onFocus)="searchProjectOwners({ query: '' })"
                          [placeholder]="'projectEdit.searchProjectOwnerPlaceholder' | translate"
                          field="email"
                          [minLength]="0"
                          [forceSelection]="true"
                          [dropdown]="true"
                          autocomplete="new-password"
                          styleClass="w-full"
                          inputStyleClass="w-full"
                        >
                          <ng-template let-owner pTemplate="item">
                            <div class="flex flex-col py-2">
                              <div class="font-semibold">{{ owner.email }}</div>
                              <div class="text-sm text-secondary">
                                @if (owner.fullName) {
                                <span>{{ owner.fullName }}</span>
                                } @if (owner.company) {
                                <span> - {{ owner.company }}</span>
                                }
                              </div>
                            </div>
                          </ng-template>
                        </p-autocomplete>
                        @if (projectForm.get('projectOwner')?.invalid &&
                        projectForm.get('projectOwner')?.touched) {
                        <div class="mt-2">
                          <p-message severity="error">{{
                            'projectEdit.projectOwnerRequired' | translate
                          }}</p-message>
                        </div>
                        }
                      </div>

                      <div>
                        <label for="ownerUrl" class="form-label mb-2 block">{{
                          'projectEdit.ownerUrl' | translate
                        }}</label>
                        <input
                          id="ownerUrl"
                          type="url"
                          pInputText
                          formControlName="ownerUrl"
                          [placeholder]="'projectEdit.ownerUrlPlaceholder' | translate"
                          (input)="onUrlFieldInput('ownerUrl')"
                          (blur)="onUrlFieldBlur('ownerUrl')"
                          class="w-full"
                        />
                        @if (projectForm.get('ownerUrl')?.invalid &&
                        projectForm.get('ownerUrl')?.touched && projectForm.get('ownerUrl')?.value
                        && !isEditingOwnerUrl()) {
                        <div class="mt-2">
                          <p-message severity="error">{{
                            'projectEdit.invalidUrl' | translate
                          }}</p-message>
                        </div>
                        }
                      </div>
                    </div>
                    }
                  </div>
                </div>

                <!-- Row 2: Project Description -->
                @if (loading()) {
                <div>
                  <label class="form-label mb-2 block">{{
                    'projectEdit.description' | translate
                  }}</label>
                  <p-skeleton width="100%" height="7rem" class="rounded-md"></p-skeleton>
                </div>
                } @else {
                <div>
                  <label for="description" class="form-label mb-2 block">{{
                    'projectEdit.description' | translate
                  }}</label>
                  <textarea
                    id="description"
                    pInputText
                    formControlName="description"
                    rows="5"
                    maxlength="600"
                    (input)="onDescriptionInput($event)"
                    (keydown)="onDescriptionKeydown($event)"
                    (paste)="onDescriptionPaste($event)"
                    [placeholder]="'projectEdit.descriptionPlaceholder' | translate"
                    class="w-full resize-none"
                  ></textarea>
                  <div class="flex justify-between items-center mt-1">
                    <div>
                      @if (showDescriptionMaxLengthError()) {
                      <p-message severity="error">{{
                        'projectEdit.descriptionMaxLength' | translate
                      }}</p-message>
                      } @else if (projectForm.get('description')?.invalid &&
                      projectForm.get('description')?.touched) { @if
                      (projectForm.get('description')?.hasError('required')) {
                      <p-message severity="error">{{
                        'projectEdit.descriptionRequired' | translate
                      }}</p-message>
                      } }
                    </div>
                    <span class="text-sm text-muted">
                      {{ descriptionControl.value?.length || 0 }}/600
                    </span>
                  </div>
                </div>
                }

                <!-- Row 2b: Categories -->
                @if (loading() || categoriesLoading()) {
                <div>
                  <p-skeleton width="100px" height="1.5rem" class="rounded mb-2"></p-skeleton>
                  <p-skeleton width="100%" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                } @else {
                <div>
                  <label for="categories" class="form-label mb-2 block">{{
                    'projectEdit.categories' | translate
                  }}</label>
                  <p-multiSelect
                    id="categories"
                    formControlName="categoryIds"
                    [options]="categories"
                    optionLabel="label"
                    optionValue="id"
                    [placeholder]="'projectEdit.categoriesPlaceholder' | translate"
                    display="chip"
                    [showClear]="true"
                    class="w-full max-w-[500px]"
                    [showToggleAll]="false"
                  />
                  @if (projectForm.get('categories')?.invalid &&
                  projectForm.get('categories')?.touched) {
                  <div class="mt-2">
                    @if (projectForm.get('categories')?.hasError('required')) {
                    <p-message severity="error">{{
                      'projectEdit.categoriesRequired' | translate
                    }}</p-message>
                    }
                  </div>
                  }
                </div>
                }

                <!-- Row 3: Location with Checkboxes -->
                @if (loading()) {
                <div class="flex items-end gap-6 flex-wrap">
                  <div class="flex items-center gap-2">
                    <i class="pi pi-map-marker text-muted"></i>
                    <div>
                      <label class="form-label mb-2 block">{{
                        'projectEdit.location' | translate
                      }}</label>
                      <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                  </div>
                  <p-skeleton width="100px" height="1.5rem" class="rounded mb-3"></p-skeleton>
                  <p-skeleton width="140px" height="1.5rem" class="rounded mb-3"></p-skeleton>
                </div>
                } @else {
                <div class="flex items-end gap-6 flex-wrap">
                  <!-- Location Select -->
                  <div class="flex items-center gap-2">
                    <i class="pi pi-map-marker text-muted"></i>
                    <div>
                      <label for="location" class="form-label mb-2 block">{{
                        'projectEdit.location' | translate
                      }}</label>
                      <p-select
                        id="location"
                        formControlName="location"
                        [options]="countries"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="'projectEdit.locationPlaceholder' | translate"
                        [filter]="true"
                        [filterPlaceholder]="'projectEdit.locationFilterPlaceholder' | translate"
                        [showClear]="true"
                        class="w-full md:w-60"
                        [class.ng-invalid]="locationControl.invalid && locationControl.touched"
                        [class.ng-dirty]="locationControl.touched"
                      />
                      @if (locationControl.invalid && locationControl.touched) {
                      <p-message severity="error" class="mt-2 block">{{
                        getLocationErrorMessage()
                      }}</p-message>
                      }
                    </div>
                  </div>

                  <!-- Verified Checkbox -->
                  <div class="flex items-center gap-2 mb-3">
                    <p-checkbox formControlName="verified" [binary]="true" inputId="verified" />
                    <label for="verified" class="form-label cursor-pointer">
                      {{ 'projectEdit.verified' | translate }}
                    </label>
                  </div>

                  <!-- MiCA Compliant Checkbox -->
                  <div class="flex items-center gap-2 mb-3">
                    <p-checkbox
                      formControlName="micaCompliant"
                      [binary]="true"
                      inputId="micaCompliant"
                    />
                    <label for="micaCompliant" class="form-label cursor-pointer">
                      {{ 'projectEdit.micaCompliant' | translate }}
                    </label>
                  </div>
                </div>
                }

                <!-- Row 4: Social Links -->
                @if (loading()) {
                <div class="flex flex-col gap-2">
                  <p-skeleton width="80px" height="1.5rem" class="rounded"></p-skeleton>
                  <div class="flex items-center gap-4 flex-wrap">
                    <!-- Twitter -->
                    <div class="flex items-center gap-1">
                      <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                      <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                    <!-- Linkedin -->
                    <div class="flex items-center gap-1">
                      <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                      <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                    <!-- Telegram -->
                    <div class="flex items-center gap-1">
                      <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                      <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                    <!-- Discord -->
                    <div class="flex items-center gap-1">
                      <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                      <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                  </div>
                </div>
                } @else {
                <div class="flex flex-col gap-2">
                  <label class="text-label-md">{{ 'projectEdit.followOn' | translate }}</label>

                  <div class="flex gap-4 flex-wrap">
                    <!-- Twitter -->
                    <div class="flex flex-col gap-1 items-center">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-twitter text-info"></i>
                        <input
                          type="url"
                          pInputText
                          formControlName="twitterUrl"
                          [placeholder]="'projectEdit.twitterPlaceholder' | translate"
                          (input)="onUrlFieldInput('twitterUrl')"
                          (blur)="onUrlFieldBlur('twitterUrl')"
                          class="w-full md:w-56"
                        />
                      </div>
                      @if (projectForm.get('twitterUrl')?.invalid &&
                      projectForm.get('twitterUrl')?.touched && projectForm.get('twitterUrl')?.value
                      && !isEditingTwitterUrl()) {
                      <p-message severity="error" class="ml-6">{{
                        'projectEdit.invalidUrl' | translate
                      }}</p-message>
                      }
                    </div>

                    <!-- Linkedin -->
                    <div class="flex flex-col gap-1 items-center">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-linkedin text-muted"></i>
                        <input
                          type="url"
                          pInputText
                          formControlName="linkedinUrl"
                          [placeholder]="'projectEdit.linkedinPlaceholder' | translate"
                          (input)="onUrlFieldInput('linkedinUrl')"
                          (blur)="onUrlFieldBlur('linkedinUrl')"
                          class="w-full md:w-56"
                        />
                      </div>
                      @if (projectForm.get('linkedinUrl')?.invalid &&
                      projectForm.get('linkedinUrl')?.touched &&
                      projectForm.get('linkedinUrl')?.value && !isEditingLinkedinUrl()) {
                      <p-message severity="error" class="ml-6">{{
                        'projectEdit.invalidUrl' | translate
                      }}</p-message>
                      }
                    </div>

                    <!-- Telegram -->
                    <div class="flex flex-col gap-1 items-center">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-send text-info"></i>
                        <input
                          type="url"
                          pInputText
                          formControlName="telegramUrl"
                          [placeholder]="'projectEdit.telegramPlaceholder' | translate"
                          (input)="onUrlFieldInput('telegramUrl')"
                          (blur)="onUrlFieldBlur('telegramUrl')"
                          class="w-full md:w-56"
                        />
                      </div>
                      @if (projectForm.get('telegramUrl')?.invalid &&
                      projectForm.get('telegramUrl')?.touched &&
                      projectForm.get('telegramUrl')?.value && !isEditingTelegramUrl()) {
                      <p-message severity="error" class="ml-6">{{
                        'projectEdit.invalidUrl' | translate
                      }}</p-message>
                      }
                    </div>

                    <!-- Discord -->
                    <div class="flex flex-col gap-1 items-center">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-discord text-brand-primary"></i>
                        <input
                          type="url"
                          pInputText
                          formControlName="discordUrl"
                          [placeholder]="'projectEdit.discordPlaceholder' | translate"
                          (input)="onUrlFieldInput('discordUrl')"
                          (blur)="onUrlFieldBlur('discordUrl')"
                          class="w-full md:w-56"
                        />
                      </div>
                      @if (projectForm.get('discordUrl')?.invalid &&
                      projectForm.get('discordUrl')?.touched && projectForm.get('discordUrl')?.value
                      && !isEditingDiscordUrl()) {
                      <p-message severity="error" class="ml-6">{{
                        'projectEdit.invalidUrl' | translate
                      }}</p-message>
                      }
                    </div>
                  </div>
                </div>
                }

                <!-- Row 5: Documentation Links -->
                @if (loading()) {
                <div class="flex flex-col gap-2">
                  <p-skeleton width="100px" height="1.5rem" class="rounded"></p-skeleton>
                  <div class="flex items-center gap-4 flex-wrap">
                    <!-- Whitepaper -->
                    <div class="flex items-center gap-1">
                      <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                      <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                    <!-- Tokenomics -->
                    <div class="flex items-center gap-1">
                      <p-skeleton width="1rem" height="1rem" class="rounded"></p-skeleton>
                      <p-skeleton width="224px" height="3rem" class="rounded-md"></p-skeleton>
                    </div>
                  </div>
                </div>
                } @else {
                <div class="flex flex-col gap-2">
                  <label class="text-label-md">{{ 'projectEdit.documentation' | translate }}</label>

                  <div class="flex items-center gap-4 flex-wrap">
                    <!-- Whitepaper -->
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-file-pdf text-error"></i>
                        <input
                          type="url"
                          pInputText
                          formControlName="whitepaperUrl"
                          [placeholder]="'projectEdit.whitepaperPlaceholder' | translate"
                          (input)="onUrlFieldInput('whitepaperUrl')"
                          (blur)="onUrlFieldBlur('whitepaperUrl')"
                          class="w-full md:w-56"
                        />
                      </div>
                      @if (projectForm.get('whitepaperUrl')?.invalid &&
                      projectForm.get('whitepaperUrl')?.touched &&
                      projectForm.get('whitepaperUrl')?.value && !isEditingWhitepaperUrl()) {
                      <p-message severity="error" class="ml-6">{{
                        'projectEdit.invalidUrl' | translate
                      }}</p-message>
                      }
                    </div>

                    <!-- Tokenomics -->
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-1">
                        <i class="pi pi-chart-pie text-brand-primary"></i>
                        <input
                          type="url"
                          pInputText
                          formControlName="tokenomicsUrl"
                          [placeholder]="'projectEdit.tokenomicsPlaceholder' | translate"
                          (input)="onUrlFieldInput('tokenomicsUrl')"
                          (blur)="onUrlFieldBlur('tokenomicsUrl')"
                          class="w-full md:w-56"
                        />
                      </div>
                      @if (projectForm.get('tokenomicsUrl')?.invalid &&
                      projectForm.get('tokenomicsUrl')?.touched &&
                      projectForm.get('tokenomicsUrl')?.value && !isEditingTokenomicsUrl()) {
                      <p-message severity="error" class="ml-6">{{
                        'projectEdit.invalidUrl' | translate
                      }}</p-message>
                      }
                    </div>
                  </div>
                </div>
                }
              </div>
            </p-card>
            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('1')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel0Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Token Information Card -->
        <p-accordion-panel value="1">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.tokenInformation' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <p-card>
              @if (loading()) {
              <div class="space-y-3">
                <!-- Token Name Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="80px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Token Symbol Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="60px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Token Address Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="70px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Total Supply Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="90px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Swap On Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="70px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Claim On Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="70px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Public Sale Price Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="140px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Private Sale Price Row -->
                <div class="flex justify-between items-center py-2">
                  <p-skeleton width="140px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="240px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
              </div>
              } @else {
              <div class="space-y-3">
                <!-- Name -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <label class="form-label">{{ 'projectEdit.tokenName' | translate }}</label>
                  <div class="w-full md:max-w-xs">
                    <input
                      type="text"
                      pInputText
                      formControlName="tokenName"
                      [placeholder]="'projectEdit.tokenNamePlaceholder' | translate"
                      class="w-full"
                    />
                    @if (projectForm.get('tokenName')?.invalid &&
                    projectForm.get('tokenName')?.touched) {
                    <div class="mt-2">
                      <p-message severity="error">{{
                        getErrorMessage(
                          projectForm.get('tokenName'),
                          'projectEdit.tokenName' | translate
                        )
                      }}</p-message>
                    </div>
                    }
                  </div>
                </div>

                <!-- Symbol -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <label class="form-label">{{ 'projectEdit.tokenSymbol' | translate }}</label>
                  <div class="w-full md:max-w-xs">
                    <input
                      type="text"
                      pInputText
                      formControlName="tokenSymbol"
                      [placeholder]="'projectEdit.tokenSymbolPlaceholder' | translate"
                      class="w-full"
                    />
                    @if (projectForm.get('tokenSymbol')?.invalid &&
                    projectForm.get('tokenSymbol')?.touched) {
                    <div class="mt-2">
                      <p-message severity="error">{{
                        getErrorMessage(
                          projectForm.get('tokenSymbol'),
                          'projectEdit.tokenSymbol' | translate
                        )
                      }}</p-message>
                    </div>
                    }
                  </div>
                </div>

                <!-- Address -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <label class="form-label">{{ 'projectEdit.tokenAddress' | translate }}</label>
                  <div class="w-full md:max-w-xs">
                    <input
                      type="text"
                      pInputText
                      formControlName="tokenAddress"
                      [placeholder]="'projectEdit.tokenAddressPlaceholder' | translate"
                      class="w-full"
                    />
                    @if (projectForm.get('tokenAddress')?.invalid &&
                    projectForm.get('tokenAddress')?.touched) {
                    <div class="mt-2">
                      <p-message severity="error">{{
                        getErrorMessage(
                          projectForm.get('tokenAddress'),
                          'projectEdit.tokenAddress' | translate
                        )
                      }}</p-message>
                    </div>
                    }
                  </div>
                </div>

                <!-- Total Supply -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.totalSupply' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.totalSupplyTooltip' | translate"
                    ></i>
                  </div>
                  <div class="w-full md:max-w-xs">
                    <p-inputGroup class="w-full">
                      <p-inputNumber
                        formControlName="totalSupply"
                        [placeholder]="'projectEdit.totalSupplyPlaceholder' | translate"
                        [showButtons]="false"
                        [useGrouping]="true"
                        class="text-right"
                      />
                      <p-inputGroupAddon>{{
                        projectForm.get('tokenSymbol')?.value || 'PTK'
                      }}</p-inputGroupAddon>
                    </p-inputGroup>
                    @if (projectForm.get('totalSupply')?.invalid &&
                    projectForm.get('totalSupply')?.touched) {
                    <div class="mt-2">
                      <p-message severity="error">{{
                        getErrorMessage(
                          projectForm.get('totalSupply'),
                          'projectEdit.totalSupply' | translate
                        )
                      }}</p-message>
                    </div>
                    }
                  </div>
                </div>

                <!-- Swap On -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.swapOn' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.swapOnTooltip' | translate"
                    ></i>
                  </div>
                  <div class="w-full md:max-w-xs">
                    <p-select
                      id="swapOn"
                      formControlName="swapOn"
                      [options]="blockchains"
                      optionLabel="label"
                      optionValue="value"
                      [placeholder]="'projectEdit.swapOnPlaceholder' | translate"
                      [filter]="true"
                      [filterPlaceholder]="'projectEdit.swapOnFilterPlaceholder' | translate"
                      [showClear]="true"
                      class="w-full"
                      [class.ng-invalid]="swapOnControl.invalid && swapOnControl.touched"
                      [class.ng-dirty]="swapOnControl.touched"
                    />
                    @if (swapOnControl.invalid && swapOnControl.touched) {
                    <p-message severity="error" class="mt-2 block">{{
                      getSwapOnErrorMessage()
                    }}</p-message>
                    }
                  </div>
                </div>

                <!-- Claim On -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.claimOn' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.claimOnTooltip' | translate"
                    ></i>
                  </div>
                  <div class="w-full md:max-w-xs">
                    <p class="text-body-md text-primary">
                      {{ 'projectEdit.claimOnText' | translate }}
                    </p>
                  </div>
                </div>
              </div>
              }
            </p-card>
            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('2')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel1Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Sale Information Card -->
        <p-accordion-panel value="2">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.saleInformation' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <p-card>
              @if (loading()) {
              <div class="space-y-3">
                <!-- SoftCap Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="60px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="180px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- HardCap Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="60px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="180px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Min/Max Allocation Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="120px" height="1.5rem" class="rounded"></p-skeleton>
                  <div class="flex items-center gap-2">
                    <p-skeleton width="100px" height="3rem" class="rounded-md"></p-skeleton>
                    <span class="text-gray-400">-</span>
                    <p-skeleton width="100px" height="3rem" class="rounded-md"></p-skeleton>
                  </div>
                </div>
                <!-- Total Tokens for Sale Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="140px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="180px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Pool Types Row -->
                <div class="flex justify-between items-center py-2 border-b border-gray-700">
                  <p-skeleton width="80px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="300px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
                <!-- Unsold Tokens Row -->
                <div class="flex justify-between items-center py-2">
                  <p-skeleton width="100px" height="1.5rem" class="rounded"></p-skeleton>
                  <p-skeleton width="300px" height="3rem" class="rounded-md"></p-skeleton>
                </div>
              </div>
              } @else {
              <div class="space-y-3">
                <!-- SoftCap -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.softCap' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.softCapTooltip' | translate"
                    ></i>
                  </div>
                  <p-inputGroup class="w-full md:max-w-xs">
                    <p-inputNumber
                      formControlName="softCap"
                      [placeholder]="'projectEdit.softCapPlaceholder' | translate"
                      [showButtons]="false"
                      [useGrouping]="true"
                      class="text-right"
                    />
                    <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
                  </p-inputGroup>
                </div>

                <!-- HardCap -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.hardCap' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.hardCapTooltip' | translate"
                    ></i>
                  </div>
                  <p-inputGroup class="w-full md:max-w-xs">
                    <p-inputNumber
                      formControlName="hardCap"
                      [placeholder]="'projectEdit.hardCapPlaceholder' | translate"
                      [showButtons]="false"
                      [useGrouping]="true"
                      class="text-right"
                    />
                    <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
                  </p-inputGroup>
                </div>

                <!-- Min/Max Allocation -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{
                      'projectEdit.minMaxAllocation' | translate
                    }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.minMaxAllocationTooltip' | translate"
                    ></i>
                  </div>
                  <div class="flex items-center gap-2 text-white text-sm w-full md:w-auto">
                    <p-inputGroup class="flex-1 md:w-20">
                      <p-inputNumber
                        formControlName="minAllocation"
                        [placeholder]="'projectEdit.minAllocationPlaceholder' | translate"
                        [showButtons]="false"
                      />
                      <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
                    </p-inputGroup>
                    <span class="text-gray-400">{{ 'projectEdit.dash' | translate }}</span>
                    <p-inputGroup class="flex-1 md:w-24">
                      <p-inputNumber
                        formControlName="maxAllocation"
                        [placeholder]="'projectEdit.maxAllocationPlaceholder' | translate"
                        [showButtons]="false"
                      />
                      <p-inputGroupAddon>{{ 'projectEdit.usdt' | translate }}</p-inputGroupAddon>
                    </p-inputGroup>
                  </div>
                </div>
                <!-- Total Tokens for Sale -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{
                      'projectEdit.totalTokensForSale' | translate
                    }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.totalTokensTooltip' | translate"
                    ></i>
                  </div>
                  <p-inputGroup class="w-full md:max-w-xs">
                    <p-inputNumber
                      formControlName="totalTokensForSale"
                      [placeholder]="'projectEdit.totalTokensForSalePlaceholder' | translate"
                      [showButtons]="false"
                      [useGrouping]="true"
                      class="text-right"
                    />
                    <p-inputGroupAddon>{{
                      projectForm.get('tokenSymbol')?.value || 'PTK'
                    }}</p-inputGroupAddon>
                  </p-inputGroup>
                </div>

                <!-- Pool Types -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-gray-700 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.poolTypes' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.poolTypesTooltip' | translate"
                    ></i>
                  </div>
                  <div class="w-full md:max-w-xs">
                    <p class="text-body-md text-primary text-right">
                      {{ poolTypesControl.value }}
                    </p>
                  </div>
                </div>

                <!-- Unsold Tokens -->
                <div
                  class="flex flex-col md:flex-row md:justify-between md:items-center py-2 gap-2"
                >
                  <div class="flex items-center gap-1.5">
                    <label class="form-label">{{ 'projectEdit.unsoldTokens' | translate }}</label>
                    <i
                      class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                      [pTooltip]="'projectEdit.unsoldTokensTooltip' | translate"
                    ></i>
                  </div>
                  <input
                    type="text"
                    pInputText
                    formControlName="unsoldTokens"
                    [placeholder]="'projectEdit.unsoldTokensPlaceholder' | translate"
                    class="w-full md:text-right bg-transparent border-none text-white focus:ring-0 md:flex-1 md:max-w-2xl"
                  />
                </div>
              </div>
              }
            </p-card>
            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('3')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel2Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Investment Rounds Card -->
        <!-- Investment Rounds Card -->
        <p-accordion-panel value="3">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.investmentRounds' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <!-- Auto-link dates checkbox -->
            <div class="flex items-center gap-2 mb-4">
              <p-checkbox
                [formControl]="autoLinkDatesControl"
                inputId="autoLinkDates"
                [binary]="true"
              ></p-checkbox>
              <label for="autoLinkDates" class="form-label cursor-pointer">
                {{ 'projectEdit.autoLinkDates' | translate }}
              </label>
              <i
                class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                [pTooltip]="'projectEdit.autoLinkDatesTooltip' | translate"
              ></i>
            </div>

            <!-- Template for investment rounds content -->
            <ng-template #investmentRoundsContent>
              @if (loading()) {
              <div class="space-y-6">
                <!-- Private Sale Skeleton -->
                <div class="flex items-start gap-4">
                  <div class="flex flex-col items-center">
                    <p-skeleton width="2rem" height="2rem" class="rounded-full"></p-skeleton>
                    <p-skeleton width="2px" height="4rem" class="mt-2"></p-skeleton>
                  </div>
                  <div class="flex-1 space-y-6">
                    <p-skeleton width="120px" height="1.5rem" class="rounded"></p-skeleton>
                    <div class="space-y-6">
                      <!-- Exchange Rate Label (always visible) -->
                      <div class="flex items-center gap-2">
                        <label class="form-label whitespace-nowrap">{{
                          'projectEdit.oneUsdtEquals' | translate
                        }}</label>
                        <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                      </div>
                      <!-- Start Date -->
                      <div>
                        <label class="form-label mb-2 block">{{
                          'projectEdit.startDateAndTime' | translate
                        }}</label>
                        <p-skeleton
                          width="100%"
                          height="3rem"
                          class="rounded-md max-w-sm"
                        ></p-skeleton>
                      </div>
                      <!-- End Date -->
                      <div>
                        <label class="form-label mb-2 block">{{
                          'projectEdit.endDateAndTime' | translate
                        }}</label>
                        <p-skeleton
                          width="100%"
                          height="3rem"
                          class="rounded-md max-w-sm"
                        ></p-skeleton>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Public Sale Skeleton -->
                <div class="flex items-start gap-4">
                  <div class="flex flex-col items-center">
                    <p-skeleton width="2rem" height="2rem" class="rounded-full"></p-skeleton>
                    <p-skeleton width="2px" height="4rem" class="mt-2"></p-skeleton>
                  </div>
                  <div class="flex-1 space-y-6">
                    <p-skeleton width="120px" height="1.5rem" class="rounded"></p-skeleton>
                    <div class="space-y-6">
                      <!-- Exchange Rate Label (always visible) -->
                      <div class="flex items-center gap-2">
                        <label class="form-label whitespace-nowrap">{{
                          'projectEdit.oneUsdtEquals' | translate
                        }}</label>
                        <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                      </div>
                      <!-- Start Date -->
                      <div>
                        <label class="form-label mb-2 block">{{
                          'projectEdit.startDateAndTime' | translate
                        }}</label>
                        <p-skeleton
                          width="100%"
                          height="3rem"
                          class="rounded-md max-w-sm"
                        ></p-skeleton>
                      </div>
                      <!-- End Date -->
                      <div>
                        <label class="form-label mb-2 block">{{
                          'projectEdit.endDateAndTime' | translate
                        }}</label>
                        <p-skeleton
                          width="100%"
                          height="3rem"
                          class="rounded-md max-w-sm"
                        ></p-skeleton>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- TGE Date Skeleton -->
                <div class="flex items-start gap-4">
                  <p-skeleton width="2rem" height="2rem" class="rounded-full"></p-skeleton>
                  <div class="flex-1 space-y-6">
                    <p-skeleton width="100px" height="1.5rem" class="rounded"></p-skeleton>
                    <div class="space-y-6">
                      <!-- TGE Price Label (always visible) -->
                      <div class="flex items-center gap-2">
                        <label class="form-label whitespace-nowrap">{{
                          'projectEdit.oneUsdtEquals' | translate
                        }}</label>
                        <p-skeleton width="200px" height="3rem" class="rounded-md"></p-skeleton>
                      </div>
                      <!-- TGE Date -->
                      <div>
                        <label class="form-label mb-2 block">{{
                          'projectEdit.tgeDateAndTime' | translate
                        }}</label>
                        <p-skeleton
                          width="100%"
                          height="3rem"
                          class="rounded-md max-w-sm"
                        ></p-skeleton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              } @else {
              <p-stepper [value]="1" orientation="vertical">
                <!-- Private Sale Step -->
                <p-step-item [value]="1">
                  <p-step>{{ 'projectEdit.privateSale' | translate }}</p-step>
                  <p-step-panel>
                    <ng-template #content let-activateCallback="activateCallback">
                      <div class="flex flex-col gap-6 py-4 pr-4">
                        <!-- Exchange Rate -->
                        <div class="flex flex-col md:flex-row md:items-center gap-2">
                          <div class="flex items-center gap-1.5">
                            <label class="form-label whitespace-nowrap">{{
                              'projectEdit.privateSalePrice' | translate
                            }}</label>
                            <i
                              class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                              [pTooltip]="'projectEdit.privateSalePriceTooltip' | translate"
                            ></i>
                          </div>
                          <span class="form-label whitespace-nowrap">{{
                            'projectEdit.oneUsdtEquals' | translate
                          }}</span>
                          <p-inputGroup class="w-full md:max-w-[200px]">
                            <p-inputNumber
                              formControlName="privateSalePrice"
                              [showButtons]="false"
                              [useGrouping]="true"
                            />
                            <p-inputGroupAddon>{{
                              projectForm.get('tokenSymbol')?.value || 'PTK'
                            }}</p-inputGroupAddon>
                          </p-inputGroup>
                        </div>

                        <div class="flex flex-col md:flex-row gap-6">
                          <!-- Start Date -->
                          <div>
                            <label for="privateSaleStart" class="form-label mb-2 block">{{
                              'projectEdit.startDateAndTime' | translate
                            }}</label>
                            <p-datePicker
                              [inline]="!isMobile()"
                              id="privateSaleStart"
                              formControlName="privateSaleStart"
                              [showTime]="true"
                              [hourFormat]="'24'"
                              dateFormat="dd/mm/yy"
                            />
                            @if (projectForm.get('privateSaleStart')?.invalid &&
                            projectForm.get('privateSaleStart')?.touched) {
                            <div class="mt-2">
                              <p-message severity="error">{{
                                getErrorMessage(
                                  projectForm.get('privateSaleStart'),
                                  'projectEdit.startDateAndTime' | translate
                                )
                              }}</p-message>
                            </div>
                            }
                          </div>

                          <!-- End Date (hidden when auto-link is enabled) -->
                          @if (!autoLinkDates()) {
                          <div>
                            <label for="privateSaleEnd" class="form-label mb-2 block">{{
                              'projectEdit.endDateAndTime' | translate
                            }}</label>
                            <p-datePicker
                              [inline]="!isMobile()"
                              id="privateSaleEnd"
                              formControlName="privateSaleEnd"
                              [showTime]="true"
                              [hourFormat]="'24'"
                              dateFormat="dd/mm/yy"
                            />
                            @if (projectForm.get('privateSaleEnd')?.invalid &&
                            projectForm.get('privateSaleEnd')?.touched) {
                            <div class="mt-2">
                              <p-message severity="error">{{
                                getErrorMessage(
                                  projectForm.get('privateSaleEnd'),
                                  'projectEdit.endDateAndTime' | translate
                                )
                              }}</p-message>
                            </div>
                            }
                          </div>
                          }
                        </div>
                      </div>

                      <div class="py-4">
                        <p-button
                          [label]="'projectEdit.next' | translate"
                          icon="pi pi-arrow-right"
                          iconPos="right"
                          (onClick)="activateCallback(2)"
                        />
                      </div>
                    </ng-template>
                  </p-step-panel>
                </p-step-item>

                <!-- Public Sale Step -->
                <p-step-item [value]="2">
                  <p-step>{{ 'projectEdit.publicSale' | translate }}</p-step>
                  <p-step-panel>
                    <ng-template #content let-activateCallback="activateCallback">
                      <div class="flex flex-col gap-6 py-4 pr-4">
                        <!-- Exchange Rate -->
                        <div class="flex flex-col md:flex-row md:items-center gap-2">
                          <div class="flex items-center gap-1.5">
                            <label class="form-label whitespace-nowrap">{{
                              'projectEdit.publicSalePrice' | translate
                            }}</label>
                            <i
                              class="pi pi-question-circle text-gray-400 text-sm cursor-help"
                              [pTooltip]="'projectEdit.publicSalePriceTooltip' | translate"
                            ></i>
                          </div>
                          <span class="form-label whitespace-nowrap">{{
                            'projectEdit.oneUsdtEquals' | translate
                          }}</span>
                          <p-inputGroup class="w-full md:max-w-[200px]">
                            <p-inputNumber
                              formControlName="publicSalePrice"
                              [showButtons]="false"
                              [useGrouping]="true"
                            />
                            <p-inputGroupAddon>{{
                              projectForm.get('tokenSymbol')?.value || 'PTK'
                            }}</p-inputGroupAddon>
                          </p-inputGroup>
                        </div>

                        <div class="flex flex-col md:flex-row gap-6">
                          <div>
                            <label for="publicSaleStart" class="form-label mb-2 block">{{
                              'projectEdit.startDateAndTime' | translate
                            }}</label>
                            <p-datePicker
                              [inline]="!isMobile()"
                              id="publicSaleStart"
                              formControlName="publicSaleStart"
                              [showTime]="true"
                              [hourFormat]="'24'"
                              dateFormat="dd/mm/yy"
                            />
                            @if (projectForm.get('publicSaleStart')?.invalid &&
                            projectForm.get('publicSaleStart')?.touched) {
                            <div class="mt-2">
                              <p-message severity="error">{{
                                getErrorMessage(
                                  projectForm.get('publicSaleStart'),
                                  'projectEdit.startDateAndTime' | translate
                                )
                              }}</p-message>
                            </div>
                            }
                          </div>

                          <!-- End Date (hidden when auto-link is enabled) -->
                          @if (!autoLinkDates()) {
                          <div>
                            <label for="publicSaleEnd" class="form-label mb-2 block">{{
                              'projectEdit.endDateAndTime' | translate
                            }}</label>
                            <p-datePicker
                              [inline]="!isMobile()"
                              id="publicSaleEnd"
                              formControlName="publicSaleEnd"
                              [showTime]="true"
                              [hourFormat]="'24'"
                              dateFormat="dd/mm/yy"
                            />
                            @if (projectForm.get('publicSaleEnd')?.invalid &&
                            projectForm.get('publicSaleEnd')?.touched) {
                            <div class="mt-2">
                              <p-message severity="error">{{
                                getErrorMessage(
                                  projectForm.get('publicSaleEnd'),
                                  'projectEdit.endDateAndTime' | translate
                                )
                              }}</p-message>
                            </div>
                            }
                          </div>
                          }
                        </div>
                        <!-- Start Date -->
                      </div>

                      <div class="flex gap-2 py-4">
                        <p-button
                          [label]="'projectEdit.back' | translate"
                          severity="secondary"
                          icon="pi pi-arrow-left"
                          (onClick)="activateCallback(1)"
                        />
                        <p-button
                          [label]="'projectEdit.next' | translate"
                          icon="pi pi-arrow-right"
                          iconPos="right"
                          (onClick)="activateCallback(3)"
                        />
                      </div>
                    </ng-template>
                  </p-step-panel>
                </p-step-item>

                <!-- TGE Date Step -->
                <p-step-item [value]="3">
                  <p-step>{{ 'projectEdit.tgeDate' | translate }}</p-step>
                  <p-step-panel>
                    <ng-template #content let-activateCallback="activateCallback">
                      <div class="flex flex-col gap-6 py-4 pr-4">
                        <!-- TGE Price -->
                        <div class="flex flex-col md:flex-row md:items-center gap-2">
                          <label class="form-label whitespace-nowrap">{{
                            'projectEdit.oneUsdtEquals' | translate
                          }}</label>
                          <p-inputGroup class="w-full md:max-w-[200px]">
                            <p-inputNumber
                              formControlName="tgePrice"
                              [showButtons]="false"
                              [useGrouping]="true"
                            />
                            <p-inputGroupAddon>{{
                              projectForm.get('tokenSymbol')?.value || 'PTK'
                            }}</p-inputGroupAddon>
                          </p-inputGroup>
                        </div>

                        <!-- TGE Date -->
                        <div>
                          <label for="tgeDate" class="form-label mb-2 block">{{
                            'projectEdit.tgeDateAndTime' | translate
                          }}</label>
                          <p-datePicker
                            [inline]="!isMobile()"
                            id="tgeDate"
                            formControlName="tgeDate"
                            [showTime]="true"
                            [hourFormat]="'24'"
                            dateFormat="dd/mm/yy"
                          />
                          @if (projectForm.get('tgeDate')?.invalid &&
                          projectForm.get('tgeDate')?.touched) {
                          <div class="mt-2">
                            <p-message severity="error">{{
                              getErrorMessage(
                                projectForm.get('tgeDate'),
                                'projectEdit.tgeDateAndTime' | translate
                              )
                            }}</p-message>
                          </div>
                          }
                        </div>
                      </div>

                      <div class="py-4">
                        <p-button
                          [label]="'projectEdit.back' | translate"
                          severity="secondary"
                          icon="pi pi-arrow-left"
                          (onClick)="activateCallback(2)"
                        />
                      </div>
                    </ng-template>
                  </p-step-panel>
                </p-step-item>
              </p-stepper>
              }
            </ng-template>

            <!-- Desktop: Wrap in p-card -->
            <p-card class="hidden md:block">
              <ng-container *ngTemplateOutlet="investmentRoundsContent"></ng-container>
            </p-card>

            <!-- Mobile: Direct content without card -->
            <div class="block md:hidden">
              <ng-container *ngTemplateOutlet="investmentRoundsContent"></ng-container>
            </div>

            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('4')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel3Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Tabs Section -->
        <!-- Project Overview Panel -->
        <p-accordion-panel value="4">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.projectOverview.title' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <!-- Desktop: Wrap in p-card -->
            <p-card class="hidden md:block">
              @if (loading()) {
              <!-- Skeleton for content -->
              <p-skeleton width="100%" height="400px"></p-skeleton>
              } @else {
              <div class="flex flex-col gap-4">
                <h3 class="section-title">
                  {{ 'projectEdit.projectOverview.description' | translate }}
                </h3>

                <!-- Desktop: Two Column Layout -->
                <div class="two-column-layout">
                  <!-- Left Sidebar - Overview Nav Items (Sticky) -->
                  <div class="nav-sidebar sticky top-6 self-start">
                    <div class="nav-items-container">
                      @for (navItem of overviewNavItems; track navItem.id; let i = $index) {
                      <app-overview-nav-item
                        [navItem]="navItem"
                        [index]="i"
                        [isSelected]="selectedOverviewIndex === i"
                        (navItemSelected)="onOverviewNavSelected($event)"
                      ></app-overview-nav-item>
                      }
                    </div>
                  </div>

                  <!-- Right Content - Multiple Editors -->
                  <div class="main-content">
                    <div class="flex flex-col gap-6">
                      @for (navItem of overviewNavItems; track navItem.id; let i = $index) {
                      <div [id]="'overview-section-' + i" class="scroll-mt-4">
                        <h4 class="text-heading-md mb-3">{{ navItem.title }}</h4>
                        <p-editor
                          [formControl]="
                            i === 0
                              ? ourStoryControl
                              : i === 1
                              ? problemControl
                              : i === 2
                              ? solutionControl
                              : i === 3
                              ? technologyControl
                              : i === 4
                              ? marketAudienceControl
                              : i === 5
                              ? businessModelControl
                              : roadmapControl
                          "
                          [style]="{ height: '320px' }"
                          [placeholder]="'projectEdit.projectOverview.placeholder' | translate"
                          [modules]="editorModules"
                        >
                        </p-editor>
                      </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
              }
            </p-card>

            <!-- Mobile: Accordion without card -->
            <div class="block md:hidden">
              @if (loading()) {
              <!-- Skeleton for content -->
              <p-skeleton width="100%" height="400px"></p-skeleton>
              } @else {
              <div class="flex flex-col gap-4">
                <h3 class="section-title">
                  {{ 'projectEdit.projectOverview.description' | translate }}
                </h3>

                <!-- Mobile: Accordion with Editors -->
                <p-accordion>
                  @for (navItem of overviewNavItems; track navItem.id; let i = $index) {
                  <p-accordion-panel [value]="i.toString()">
                    <p-accordion-header>
                      <h4 class="text-base font-semibold">{{ navItem.title }}</h4>
                    </p-accordion-header>
                    <p-accordion-content>
                      <p-editor
                        [formControl]="
                          i === 0
                            ? ourStoryControl
                            : i === 1
                            ? problemControl
                            : i === 2
                            ? solutionControl
                            : i === 3
                            ? technologyControl
                            : i === 4
                            ? marketAudienceControl
                            : i === 5
                            ? businessModelControl
                            : roadmapControl
                        "
                        [style]="{ height: '320px' }"
                        [placeholder]="'projectEdit.projectOverview.placeholder' | translate"
                        [modules]="editorModules"
                      >
                      </p-editor>
                    </p-accordion-content>
                  </p-accordion-panel>
                  }
                </p-accordion>
              </div>
              }
            </div>

            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('5')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel4Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Team Members Panel -->
        <p-accordion-panel value="5">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.teamMembers.title' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <p-card>
              @if (loading()) {
              <!-- Skeleton for content -->
              <p-skeleton width="100%" height="400px"></p-skeleton>
              } @else {
              <div class="flex flex-col gap-6">
                <div
                  class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4"
                >
                  <h3 class="section-title">{{ 'projectEdit.teamMembers.title' | translate }}</h3>
                  <p-button
                    [label]="'projectEdit.teamMembers.addMember' | translate"
                    icon="pi pi-plus"
                    (onClick)="openAddTeamMemberDialog()"
                    class="w-full sm:w-auto sm:ml-auto"
                  />
                </div>

                <!-- Team Members List -->
                @if (teamMembers.length > 0) {
                <div class="flex flex-wrap gap-6">
                  @for (member of teamMembers; track member.id; let i = $index) {
                  <app-team-member-item
                    [member]="member"
                    [index]="i"
                    [projectId]="projectId"
                    (editMember)="onEditMember($event)"
                    (removeMember)="removeTeamMember($event)"
                  ></app-team-member-item>
                  }
                </div>
                } @else {
                <div class="empty-state">
                  <i class="pi pi-users empty-state-icon"></i>
                  <h4 class="empty-state-title">
                    {{ 'projectEdit.teamMembers.noMembers' | translate }}
                  </h4>
                  <p class="empty-state-description">
                    {{ 'projectEdit.teamMembers.noMembersDescription' | translate }}
                  </p>
                  <p-button
                    [label]="'projectEdit.teamMembers.addFirstMember' | translate"
                    icon="pi pi-plus"
                    (onClick)="openAddTeamMemberDialog()"
                  />
                </div>
                }
              </div>
              }
            </p-card>
            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('6')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel4Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Vesting Categories & Token Allocation Panel -->
        <p-accordion-panel value="6">
          <p-accordion-header>
            <h3 class="section-title">
              {{ 'projectEdit.vestingCategories.title' | translate }}
            </h3>
          </p-accordion-header>
          <p-accordion-content>
            <p-card>
              @if (loading()) {
              <!-- Skeleton for content -->
              <p-skeleton width="100%" height="400px"></p-skeleton>
              } @else {
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Left Side: Token Allocation Chart -->
                <div class="lg:col-span-1">
                  <p-card>
                    <h3 class="section-title mb-6">
                      {{ 'projectEdit.tokenAllocation.title' | translate }}
                    </h3>

                    <!-- Token Supply Info -->
                    <div class="space-y-2 mb-6">
                      <div class="flex justify-between text-sm">
                        <span class="text-gray-400">{{
                          'projectEdit.tokenAllocation.tokenSupply' | translate
                        }}</span>
                        <span class="font-semibold">{{
                          projectForm.get('totalSupply')?.value | number
                        }}</span>
                      </div>
                      <div class="flex justify-between text-sm">
                        <span class="text-gray-400">{{
                          'projectEdit.tokenAllocation.privateSale' | translate
                        }}</span>
                        <span class="font-semibold">{{
                          projectForm.get('softCap')?.value | number
                        }}</span>
                      </div>
                      <div class="flex justify-between text-sm">
                        <span class="text-gray-400">{{
                          'projectEdit.tokenAllocation.publicSale' | translate
                        }}</span>
                        <span class="font-semibold">{{
                          projectForm.get('hardCap')?.value | number
                        }}</span>
                      </div>
                    </div>

                    <!-- Doughnut Chart -->
                    <div class="mb-6 flex justify-center" #chartContainer>
                      @if (chartData && chartData.labels && chartData.labels.length > 0) { @for
                      (item of [1]; track chartData.labels?.length + '-' +
                      (chartData.datasets?.[0]?.data?.join(',') || '')) {
                      <p-chart
                        #tokenChart
                        type="doughnut"
                        [data]="chartData"
                        [options]="chartOptions"
                        width="150"
                        height="150"
                      ></p-chart>
                      } } @else {
                      <div
                        class="w-[150px] h-[150px] border-2 border-dashed border-gray-600 rounded-full flex items-center justify-center text-gray-500 text-sm"
                      >
                        No data
                      </div>
                      }
                    </div>

                    <!-- Add Allocation Button -->
                    <div class="mb-4">
                      <p-button
                        [label]="'projectEdit.tokenAllocation.addAllocation' | translate"
                        icon="pi pi-plus"
                        (onClick)="openAddAllocationDialog()"
                        [outlined]="true"
                        size="small"
                        class="w-full"
                      />
                    </div>

                    <!-- Token Allocation Chips -->
                    <div class="space-y-3">
                      <h4 class="text-sm font-semibold">
                        {{ 'projectEdit.tokenAllocation.allocations' | translate }}
                      </h4>

                      @if (tokenAllocationFormArray.length === 0) {
                      <div class="text-center py-4 text-gray-500 text-sm">
                        {{ 'projectEdit.tokenAllocation.emptyMessage' | translate }}
                      </div>
                      } @else {
                      <div class="flex flex-wrap gap-2">
                        @for (control of tokenAllocationFormArray.controls; track $index) {
                        <div
                          class="flex items-center gap-2 px-3 py-2 rounded-full bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 cursor-pointer hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors"
                          (click)="openEditAllocationDialog($index)"
                        >
                          <!-- Color Circle -->
                          <div
                            class="w-4 h-4 rounded-full flex-shrink-0"
                            [style.background-color]="$any(control.get('color'))?.value"
                          ></div>

                          <!-- Name and Percentage -->
                          <span class="text-sm font-medium text-surface-900 dark:text-surface-100">
                            {{ $any(control.get('name'))?.value }} ({{
                              $any(control.get('percentage'))?.value
                            }}%)
                          </span>

                          <!-- Delete Button -->
                          <button
                            type="button"
                            class="ml-1 w-5 h-5 rounded-full flex items-center justify-center hover:bg-surface-300 dark:hover:bg-surface-600 transition-colors"
                            (click)="removeAllocationItem($index); $event.stopPropagation()"
                            [attr.aria-label]="'projectEdit.remove' | translate"
                          >
                            <i
                              class="pi pi-times text-xs text-surface-600 dark:text-surface-400"
                            ></i>
                          </button>
                        </div>
                        }
                      </div>

                      <!-- Total Percentage Display -->
                      <div class="flex justify-between items-center pt-3 border-t border-gray-700">
                        <span class="font-semibold">{{
                          'projectEdit.tokenAllocation.total' | translate
                        }}</span>
                        <span
                          class="font-semibold"
                          [class.text-red-500]="getTotalAllocationPercentage() !== 100"
                          [class.text-green-500]="getTotalAllocationPercentage() === 100"
                        >
                          {{ getTotalAllocationPercentage() }}%
                        </span>
                      </div>
                      }
                    </div>
                  </p-card>
                </div>

                <!-- Right Side: Vesting Categories Table -->
                <div class="lg:col-span-2">
                  <div
                    class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6"
                  >
                    <h3 class="section-title">
                      {{ 'projectEdit.vestingCategories.title' | translate }}
                    </h3>
                    <p-button
                      [label]="'projectEdit.vestingCategories.addButton' | translate"
                      icon="pi pi-plus"
                      (onClick)="openAddVestingCategoryDialog()"
                      class="w-full sm:w-auto"
                    >
                    </p-button>
                  </div>

                  <p-table
                    [value]="vestingCategoriesFormArray.controls"
                    [tableStyle]="{ 'min-width': '50rem' }"
                  >
                    <ng-template pTemplate="header">
                      <tr>
                        <th>
                          {{ 'projectEdit.vestingCategories.categoryLabel' | translate }}
                        </th>
                        <th>
                          {{ 'projectEdit.vestingCategories.cliffPeriodLabel' | translate }}
                        </th>
                        <th>
                          {{ 'projectEdit.vestingCategories.vestingDurationLabel' | translate }}
                        </th>
                        <th>
                          {{ 'projectEdit.vestingCategories.releasingFrequencyLabel' | translate }}
                        </th>
                        <th>
                          {{ 'projectEdit.vestingCategories.tgeUnlockLabel' | translate }}
                        </th>
                        <th>
                          {{ 'projectEdit.vestingCategories.firstClaimLabel' | translate }}
                        </th>
                        <th>{{ 'projectEdit.actions' | translate }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-control let-i="rowIndex">
                      <tr>
                        <td>
                          <div class="flex flex-col gap-1">
                            <div class="font-semibold">
                              {{ control.get('category')?.value }}
                            </div>
                            <div class="flex items-center gap-2 text-sm text-gray-400">
                              <span class="font-mono whitespace-nowrap">{{
                                control.get('walletAddress')?.value | shortenAddress
                              }}</span>
                              <button
                                type="button"
                                class="pi pi-copy text-xs hover:text-white cursor-pointer"
                                (click)="copyToClipboard(control.get('walletAddress')?.value)"
                                [attr.aria-label]="'Copy wallet address'"
                              ></button>
                            </div>
                          </div>
                        </td>
                        <td>{{ control.get('cliffPeriod')?.value }}</td>
                        <td>{{ control.get('vestingDuration')?.value }}</td>
                        <td>{{ control.get('releasingFrequency')?.value }}</td>
                        <td>{{ control.get('tgeUnlock')?.value }}%</td>
                        <td>{{ control.get('firstClaim')?.value }}</td>
                        <td>
                          <div class="flex flex-row">
                            <p-button
                              icon="pi pi-pencil"
                              [rounded]="true"
                              size="small"
                              class="mr-2"
                              (onClick)="editVestingCategory(i)"
                              [ariaLabel]="'projectEdit.edit' | translate"
                            >
                            </p-button>
                            <p-button
                              icon="pi pi-trash"
                              [rounded]="true"
                              size="small"
                              (onClick)="removeVestingCategory(i)"
                              [ariaLabel]="'projectEdit.remove' | translate"
                            >
                            </p-button>
                          </div>
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                      <tr>
                        <td colspan="7" class="text-center py-4 text-gray-500">
                          {{ 'projectEdit.vestingCategories.emptyMessage' | translate }}
                        </td>
                      </tr>
                    </ng-template>
                  </p-table>
                </div>
              </div>
              }
            </p-card>
            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave('7')"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel4Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>

        <!-- Updates Panel -->
        <p-accordion-panel value="7">
          <p-accordion-header>
            <h3 class="section-title">{{ 'projectEdit.updates.title' | translate }}</h3>
          </p-accordion-header>
          <p-accordion-content>
            <p-card>
              @if (loading()) {
              <!-- Skeleton for content -->
              <p-skeleton width="100%" height="400px"></p-skeleton>
              } @else {
              <div class="flex flex-col gap-6">
                <div
                  class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4"
                >
                  <h3 class="section-title">{{ 'projectEdit.updates.title' | translate }}</h3>
                  <p-button
                    [label]="'projectEdit.updates.addUpdate' | translate"
                    icon="pi pi-plus"
                    (onClick)="openAddUpdateDialog()"
                    class="w-full sm:w-auto sm:ml-auto"
                  />
                </div>

                <!-- Updates List in Single Column Layout -->
                @if (updates.length > 0) {
                <div class="flex flex-col gap-6">
                  @for (update of updates; track update.id; let i = $index) {
                  <app-update-item
                    [update]="update"
                    [index]="i"
                    [projectId]="projectId"
                    (editUpdate)="onEditUpdate($event)"
                    (removeUpdate)="removeUpdate($event)"
                  ></app-update-item>
                  }
                </div>
                } @else {
                <div class="empty-state">
                  <i class="pi pi-megaphone empty-state-icon"></i>
                  <h4 class="empty-state-title">
                    {{ 'projectEdit.updates.noUpdates' | translate }}
                  </h4>
                  <p class="empty-state-description">
                    {{ 'projectEdit.updates.noUpdatesDescription' | translate }}
                  </p>
                  <p-button
                    [label]="'projectEdit.updates.addFirstUpdate' | translate"
                    icon="pi pi-plus"
                    (onClick)="openAddUpdateDialog()"
                  />
                </div>
                }
              </div>
              }
            </p-card>
            <!-- Save Button -->
            <div class="flex justify-end mt-4">
              <p-button
                [label]="'common.save' | translate"
                icon="pi pi-save"
                (onClick)="manualSave()"
                [loading]="autoSaving()"
                [disabled]="autoSaving() || submitting() || !isPanel4Valid()"
              />
            </div>
          </p-accordion-content>
        </p-accordion-panel>
      </p-accordion>
    </form>
  </p-card>
</div>
